import React, { useState, useEffect, useContext, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';

import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import {
  ATTACHMENT_RULE_FIELDS,
  SECTIONS,
  getFieldByKey,
  type AttachmentRuleField,
} from '../config/emailAttachmentRulesOptions';

export interface AttachmentRule {
  id: number;
  name: string;
  attachment_type: 'global' | 'conditional';
  field_key: string | null;
  trigger_value: string | null;
  file_url: string | null;
}

interface Props {
  open: boolean;
  rule: AttachmentRule | null; // null = create mode
  onClose: () => void;
  onSaved: () => void;
}

const AddEditAttachmentModal: React.FC<Props> = ({ open, rule, onClose, onSaved }) => {
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);

  const [name, setName] = useState('');
  const [attachmentType, setAttachmentType] = useState<'global' | 'conditional'>('global');
  const [fieldKey, setFieldKey] = useState('');
  const [triggerValue, setTriggerValue] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setName(rule?.name ?? '');
      setAttachmentType(rule?.attachment_type ?? 'global');
      setFieldKey(rule?.field_key ?? '');
      setTriggerValue(rule?.trigger_value ?? '');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [open, rule]);

  const selectedField: AttachmentRuleField | undefined = getFieldByKey(fieldKey);

  const handleFieldChange = (key: string) => {
    setFieldKey(key);
    setTriggerValue('');
  };

  const handleSave = async () => {
    if (!name.trim()) {
      showErrorSnackBar?.('Name is required.');
      return;
    }
    if (attachmentType === 'conditional' && (!fieldKey || !triggerValue)) {
      showErrorSnackBar?.('Condition field and value are required.');
      return;
    }
    if (!rule && !file) {
      showErrorSnackBar?.('Please select a PDF file to upload.');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('inspection_report_email_attachment_rule[name]', name);
      formData.append('inspection_report_email_attachment_rule[attachment_type]', attachmentType);
      if (attachmentType === 'conditional') {
        formData.append('inspection_report_email_attachment_rule[field_key]', fieldKey);
        formData.append('inspection_report_email_attachment_rule[trigger_value]', triggerValue);
      } else {
        formData.append('inspection_report_email_attachment_rule[field_key]', '');
        formData.append('inspection_report_email_attachment_rule[trigger_value]', '');
      }
      if (file) {
        formData.append('inspection_report_email_attachment_rule[file]', file);
      }

      if (rule) {
        await axios.patch(`/home_services/inspection_report_email_attachment_rules/${rule.id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        showSuccessSnackBar?.('Attachment updated.');
      } else {
        await axios.post('/home_services/inspection_report_email_attachment_rules', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        showSuccessSnackBar?.('Attachment created.');
      }

      onSaved();
      onClose();
    } catch (err) {
      console.error('Failed to save attachment rule:', err);
      showErrorSnackBar?.('Failed to save attachment.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{rule ? 'Edit Attachment' : 'Add Attachment'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Display Name" fullWidth value={name} onChange={(e) => setName(e.target.value)} required />

          <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              File {rule && !file && rule.file_url ? `(current: ${rule.name}.pdf — upload new to replace)` : ''}
            </Typography>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              style={{ display: 'none' }}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <Button variant="outlined" size="small" onClick={() => fileInputRef.current?.click()}>
              {file ? file.name : 'Choose PDF…'}
            </Button>
          </Box>

          <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Send Rule
            </Typography>
            <ToggleButtonGroup
              value={attachmentType}
              exclusive
              onChange={(_, val) => val && setAttachmentType(val)}
              size="small"
            >
              <ToggleButton value="global">Always (Global)</ToggleButton>
              <ToggleButton value="conditional">When Condition Met (Conditional)</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {attachmentType === 'conditional' && (
            <>
              <Divider />
              <Typography variant="body2" color="textSecondary">
                Trigger Condition
              </Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Inspection Field</InputLabel>
                <Select value={fieldKey} label="Inspection Field" onChange={(e) => handleFieldChange(e.target.value)}>
                  {SECTIONS.map((section) => [
                    <MenuItem key={`section-${section}`} disabled sx={{ fontWeight: 'bold', opacity: 1 }}>
                      — {section} —
                    </MenuItem>,
                    ...ATTACHMENT_RULE_FIELDS.filter((f) => f.section === section).map((f) => (
                      <MenuItem key={f.key} value={f.key}>
                        {f.label}
                      </MenuItem>
                    )),
                  ])}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small" disabled={!fieldKey}>
                <InputLabel>Value</InputLabel>
                <Select value={triggerValue} label="Value" onChange={(e) => setTriggerValue(e.target.value)}>
                  {(selectedField?.values ?? []).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Attachment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditAttachmentModal;
